<script lang="ts">
  import type { BeadColor } from '../catalog/types';
  import { sortColorsByHue } from '../catalog/sort';
  import { makeBlankPattern } from '../pipeline/blank';
  import {
    computePlateLayout,
    estimatedTimeMinutes,
    physicalSize,
  } from '../pipeline/estimate';
  import { applyEmptyMask, imageToPattern, type Pattern } from '../pipeline/pipeline';
  import {
    computeFitDimensions,
    hasTransparency,
    imageToAlphaMask,
    imageToGrid,
  } from '../import/image-bridge';
  import type { ResamplingMode } from '../pipeline/resize';
  import Modal from './Modal.svelte';

  /**
   * Dialog de création/import de patron (2 modes : 'import' photo, 'blank').
   *
   * **Style** : 100% classes globales (Modal + btn + field + input + seg +
   * alert + hint). Spécifique restant :
   *  - .np-source         (preview canvas source + meta)
   *  - .np-dim-row        (row inputs width/height/lock)
   *  - .np-color-grid +   (grid 11-col de boutons couleur)
   *  - .np-color + __dot
   *  - .np-section + __title (titre uppercase de section interne — dupliqué
   *    avec ExportDialog, candidat à extraction si on dépasse 2 usages)
   *  - .np-estimate-card  (carte stats bord/dim/temps)
   */

  export type DialogMode = 'import' | 'blank';

  type Props = {
    open: boolean;
    mode: DialogMode;
    palette: ReadonlyArray<BeadColor>;
    sourceImage: HTMLImageElement | undefined;
    onCancel: () => void;
    onGenerate: (pattern: Pattern) => void;
  };
  const { open, mode, palette, sourceImage, onCancel, onGenerate }: Props = $props();

  const paletteDisplay = $derived(sortColorsByHue(palette));

  let sourceAspect = $derived(
    sourceImage && sourceImage.naturalHeight > 0
      ? sourceImage.naturalWidth / sourceImage.naturalHeight
      : 1,
  );

  let width = $state(60);
  let height = $state(60);
  let aspectLock = $state(true);

  let resampling = $state<ResamplingMode>('bilinear');
  let dithering = $state(false);

  let sourceHasAlpha = $state(false);
  type TransparencyMode = 'empty' | 'fill';
  let transparencyMode = $state<TransparencyMode>('empty');
  let transparencyFillColorId = $state<string>('hama-205-01');

  let baseColorId = $state<string>('hama-205-01');

  let generating = $state(false);
  let errorMsg = $state('');

  let lastOpenedAt = 0;
  $effect(() => {
    if (!open) return;
    const now = performance.now();
    if (now === lastOpenedAt) return;
    lastOpenedAt = now;
    errorMsg = '';
    generating = false;
    resampling = 'bilinear';
    dithering = false;
    transparencyMode = 'empty';
    transparencyFillColorId = 'hama-205-01';
    baseColorId = 'hama-205-01';
    if (mode === 'import' && sourceImage) {
      const fit = computeFitDimensions(sourceAspect, 60);
      width = fit.width;
      height = fit.height;
      aspectLock = true;
      try {
        sourceHasAlpha = hasTransparency(sourceImage);
      } catch {
        sourceHasAlpha = false;
      }
    } else {
      sourceHasAlpha = false;
      width = 29;
      height = 29;
      aspectLock = false;
    }
    if (palette.length > 0) {
      const blanc = palette.find((c) => c.id === 'hama-205-01');
      const first = palette[0];
      if (blanc !== undefined) baseColorId = blanc.id;
      else if (first !== undefined) baseColorId = first.id;
    }
  });

  /**
   * Plafond de dimension provisoire (Lot N4 du pivot e-commerce) : 116 perles
   * = 4×4 plaques 29×29 = le preset XL du futur tunnel (CDC §4.4). L'ancien
   * clamp à 500 (250 000 perles ≈ 139 h de pose) était invendable en kit.
   * Sera remplacé par les variantes S/M/L/XL au Lot P3.
   */
  const MAX_CUSTOM_DIM = 116;

  function setWidth(v: number): void {
    width = clampDim(v);
    if (mode === 'import' && aspectLock) {
      height = clampDim(Math.round(width / sourceAspect));
    }
  }
  function setHeight(v: number): void {
    height = clampDim(v);
    if (mode === 'import' && aspectLock) {
      width = clampDim(Math.round(height * sourceAspect));
    }
  }
  function clampDim(v: number): number {
    if (!Number.isFinite(v)) return 1;
    return Math.max(1, Math.min(MAX_CUSTOM_DIM, Math.round(v)));
  }

  let totalBeads = $derived(width * height);
  let layout = $derived(computePlateLayout(width, height, 29, 29));
  let totalPlates = $derived(layout.cols * layout.rows);
  let dims = $derived(physicalSize(width, height));
  let timeMin = $derived(estimatedTimeMinutes(totalBeads));

  // Source preview canvas (mode import)
  let previewCanvas: HTMLCanvasElement | undefined = $state();
  $effect(() => {
    if (mode !== 'import' || !sourceImage || !previewCanvas) return;
    void sourceImage;
    const targetW = 240;
    const targetH = Math.round(240 / sourceAspect);
    previewCanvas.width = targetW;
    previewCanvas.height = targetH;
    const ctx = previewCanvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(sourceImage, 0, 0, targetW, targetH);
  });

  async function handleGenerate(): Promise<void> {
    generating = true;
    errorMsg = '';
    try {
      let pattern: Pattern;
      if (mode === 'import') {
        if (!sourceImage) throw new Error('Image source absente.');
        const fillBead =
          sourceHasAlpha && transparencyMode === 'fill'
            ? palette.find((c) => c.id === transparencyFillColorId)
            : undefined;
        const srcGrid = imageToGrid(sourceImage, {
          ...(fillBead ? { fillTransparency: fillBead.rgb } : {}),
        });
        pattern = imageToPattern(srcGrid, {
          targetWidth: width,
          targetHeight: height,
          palette,
          resampling,
          quantization: dithering ? 'dither' : 'nearest',
          plate: { width: 29, height: 29 },
        });
        if (sourceHasAlpha && transparencyMode === 'empty') {
          const mask = imageToAlphaMask(sourceImage, width, height, 128);
          pattern = applyEmptyMask(pattern, mask);
        }
      } else {
        pattern = makeBlankPattern({
          width,
          height,
          palette,
          baseColorId,
        });
      }
      onGenerate(pattern);
    } catch (err) {
      console.error('[dialog] handleGenerate échec :', err);
      errorMsg = err instanceof Error ? err.message : 'Erreur inconnue';
      generating = false;
    }
  }
</script>

<Modal
  {open}
  title={mode === 'import' ? 'Convertir une photo en patron' : 'Nouvelle grille vierge'}
  maxWidth={640}
  onClose={onCancel}
>
  {#if mode === 'import' && sourceImage}
    <section class="np-section">
      <h3 class="np-section__title">Source</h3>
      <div class="np-source">
        <canvas bind:this={previewCanvas} class="np-source__canvas"></canvas>
        <div class="np-source__meta">
          <div><strong>{sourceImage.naturalWidth} × {sourceImage.naturalHeight}</strong> px</div>
          <div class="hint">Aspect {sourceAspect.toFixed(2)}</div>
        </div>
      </div>
    </section>
  {/if}

  <section class="np-section">
    <h3 class="np-section__title">Dimensions du patron</h3>
    <div class="np-dim-row">
      <label class="field">
        <span class="field__label">Largeur</span>
        <input
          type="number"
          class="input np-dim-input"
          min="1"
          max={MAX_CUSTOM_DIM}
          value={width}
          oninput={(e) => setWidth(Number((e.currentTarget as HTMLInputElement).value))}
        />
      </label>
      <span class="np-times">×</span>
      <label class="field">
        <span class="field__label">Hauteur</span>
        <input
          type="number"
          class="input np-dim-input"
          min="1"
          max={MAX_CUSTOM_DIM}
          value={height}
          oninput={(e) => setHeight(Number((e.currentTarget as HTMLInputElement).value))}
        />
      </label>
      {#if mode === 'import'}
        <label class="field field--inline">
          <input type="checkbox" class="input--check" bind:checked={aspectLock} />
          <span class="field__label">Préserver l'aspect</span>
        </label>
      {/if}
    </div>

    <div class="np-estimate-card">
      <span>{dims.widthCm}×{dims.heightCm} cm</span>
      <span class="np-dot">·</span>
      <span>{totalBeads.toLocaleString('fr-FR')} perles</span>
      <span class="np-dot">·</span>
      <span>{totalPlates} plaque{totalPlates > 1 ? 's' : ''} 29×29</span>
      <span class="np-dot">·</span>
      <span>~{timeMin} min de pose</span>
    </div>
  </section>

  {#if mode === 'import' && sourceHasAlpha}
    <section class="np-section">
      <h3 class="np-section__title">Zones transparentes</h3>
      <div class="seg" role="group" aria-label="Mode de transparence">
        <button
          type="button"
          class="seg__item"
          class:seg__item--active={transparencyMode === 'empty'}
          onclick={() => (transparencyMode = 'empty')}
          title="Laisse les zones transparentes vides (aucune perle posée)"
        >Laisser vide</button>
        <button
          type="button"
          class="seg__item"
          class:seg__item--active={transparencyMode === 'fill'}
          onclick={() => (transparencyMode = 'fill')}
          title="Remplit les zones transparentes avec une couleur unique"
        >Remplissage</button>
      </div>
      {#if transparencyMode === 'fill'}
        <div class="np-color-grid">
          {#each paletteDisplay.slice(0, 22) as bead (bead.id)}
            <button
              type="button"
              class="np-color"
              class:np-color--active={transparencyFillColorId === bead.id}
              title={`${bead.name} (${bead.id})`}
              onclick={() => (transparencyFillColorId = bead.id)}
            >
              <span
                class="np-color__dot"
                style={`background: rgb(${bead.rgb.r}, ${bead.rgb.g}, ${bead.rgb.b})`}
              ></span>
            </button>
          {/each}
        </div>
      {/if}
      <p class="hint">
        {transparencyMode === 'empty'
          ? 'Les pixels transparents (alpha < 50%) deviennent des cellules vides : pas de perle, on voit la plaque en dessous.'
          : 'Les pixels transparents sont fusionnés avec la couleur choisie avant la quantification.'}
      </p>
    </section>
  {/if}

  {#if mode === 'import'}
    <section class="np-section">
      <h3 class="np-section__title">Méthode de conversion</h3>
      <div class="np-dim-row">
        <div class="seg" role="group" aria-label="Resampling">
          <button
            type="button"
            class="seg__item"
            class:seg__item--active={resampling === 'nearest'}
            onclick={() => (resampling = 'nearest')}
            title="Préserve les aplats (idéal pour pixel art, logos)"
          >Nearest</button>
          <button
            type="button"
            class="seg__item"
            class:seg__item--active={resampling === 'bilinear'}
            onclick={() => (resampling = 'bilinear')}
            title="Lisse les transitions (idéal pour photos)"
          >Bilinéaire</button>
        </div>
        <label class="field field--inline">
          <input type="checkbox" class="input--check" bind:checked={dithering} />
          <span class="field__label">Tramage Floyd-Steinberg</span>
        </label>
      </div>
      <p class="hint">
        {resampling === 'nearest'
          ? 'Pixel art, logos : contours nets.'
          : 'Photos : transitions douces.'}
        {dithering ? '  Tramage : mélange les couleurs pour rendre plus de teintes.' : ''}
      </p>
    </section>
  {:else}
    <section class="np-section">
      <h3 class="np-section__title">Couleur de fond</h3>
      <div class="np-color-grid">
        {#each paletteDisplay.slice(0, 22) as bead (bead.id)}
          <button
            type="button"
            class="np-color"
            class:np-color--active={baseColorId === bead.id}
            title={`${bead.name} (${bead.id})`}
            onclick={() => (baseColorId = bead.id)}
          >
            <span
              class="np-color__dot"
              style={`background: rgb(${bead.rgb.r}, ${bead.rgb.g}, ${bead.rgb.b})`}
            ></span>
          </button>
        {/each}
      </div>
      <p class="hint">La grille démarre entièrement remplie de cette couleur.</p>
    </section>
  {/if}

  <section class="np-section">
    <h3 class="np-section__title">Palette</h3>
    <div class="np-palette-info">
      <span>Hama Midi</span>
      <span class="np-dot">·</span>
      <span>{palette.length} couleurs</span>
      <span class="hint" style:margin-left="auto">
        (sélection avancée bientôt disponible)
      </span>
    </div>
  </section>

  {#if errorMsg}
    <p class="alert alert--error">⚠ {errorMsg}</p>
  {/if}

  {#snippet actions()}
    <button class="btn btn--ghost" type="button" onclick={onCancel}>Annuler</button>
    <button
      class="btn btn--primary"
      type="button"
      disabled={generating || totalBeads === 0}
      onclick={handleGenerate}
    >{generating ? 'Génération…' : mode === 'import' ? 'Générer le patron' : 'Démarrer'}</button>
  {/snippet}
</Modal>

<style>
  /* Patterns spécifiques création/import :
   *  - .np-section + __title : titres uppercase muted des sections
   *    (dupliqué avec ExportDialog — à extraire si on dépasse 2 usages)
   *  - .np-source : preview canvas image source + meta
   *  - .np-dim-row : row flex pour width × height + lock
   *  - .np-dim-input : input number plus compact (96px)
   *  - .np-estimate-card : carte stats inline
   *  - .np-color-grid + .np-color : sélecteur de couleur palette
   */

  .np-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .np-section__title {
    font-family: var(--font-body);
    font-size: var(--text-caption);
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-text-muted);
    margin: 0;
  }

  .np-source {
    display: flex;
    gap: var(--space-4);
    align-items: center;
  }
  .np-source__canvas {
    background: var(--color-surface);
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
    max-width: 240px;
    max-height: 180px;
  }
  .np-source__meta {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--text-small);
  }

  .np-dim-row {
    display: flex;
    align-items: flex-end;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .np-dim-input {
    width: 100px;
    min-height: 38px;
    padding: 6px 10px;
  }
  .np-times {
    font-size: var(--text-h3);
    color: var(--color-text-muted);
    padding-bottom: var(--space-2);
  }

  .np-estimate-card {
    font-size: var(--text-small);
    color: var(--color-text);
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    padding: 10px var(--space-3);
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .np-dot {
    opacity: 0.4;
  }

  .np-palette-info {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-small);
  }

  .np-color-grid {
    display: grid;
    grid-template-columns: repeat(11, 1fr);
    gap: 6px;
  }
  .np-color {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-1);
    border: 2px solid transparent;
    border-radius: var(--radius-sm);
    background: transparent;
    cursor: pointer;
  }
  .np-color--active {
    border-color: var(--color-accent);
  }
  .np-color:hover:not(.np-color--active) {
    background: var(--color-surface);
  }
  .np-color__dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid var(--color-border);
    display: block;
  }
</style>
